import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Icon, Spinner } from './ui/Icon.js';
import { Select } from './Select.js';
import { Segmented } from './ui/Segmented.js';
import { apiFetch } from '../lib/api.js';

const EFFORT_OPTIONS = [
  { value: 'small', label: 'Small (quickest)' },
  { value: 'medium', label: 'Small + medium' },
  { value: 'large', label: 'Any effort (small/medium/large)' },
] as const;

const PRIORITY_OPTIONS = [
  { value: 'critical', label: 'Critical only' },
  { value: 'high', label: 'High + critical' },
  { value: 'medium', label: 'Medium and above' },
  { value: 'low', label: 'Any priority' },
] as const;

interface BrandReportButtonProps {
  tenantId: string;
  brandId: string;
  brandName: string;
}

interface ReportResponse {
  data: {
    markdown: string;
    generated_at: string;
    parameters: {
      date_from: string;
      date_to: string;
      quick_max_effort: string;
      big_min_priority: string;
      top_n: number;
    };
    counts: {
      quick_wins: number;
      big_wins: number;
      gaps: number;
      competitors: number;
      engines: number;
      samples: number;
    };
  };
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'brand';
}

const REPORT_MODES = [
  { value: 'rendered', label: 'Rendered' },
  { value: 'raw', label: 'Raw markdown' },
] as const;

export function BrandReportButton({ tenantId, brandId, brandName }: BrandReportButtonProps) {
  const [stage, setStage] = useState<'idle' | 'config' | 'loading' | 'preview'>('idle');
  const [quickMaxEffort, setQuickMaxEffort] = useState<'small' | 'medium' | 'large'>('small');
  const [bigMinPriority, setBigMinPriority] = useState<'critical' | 'high' | 'medium' | 'low'>('high');
  const [topN, setTopN] = useState(5);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<ReportResponse['data'] | null>(null);
  const [renderMode, setRenderMode] = useState<'rendered' | 'raw'>('rendered');
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    setStage('loading');
    setError(null);
    try {
      const res = await apiFetch(
        `/api/v1/tenants/${tenantId}/brands/${brandId}/reports/markdown`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            quick_max_effort: quickMaxEffort,
            big_min_priority: bigMinPriority,
            top_n: topN,
          }),
        },
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `Report generation failed (${res.status})`);
      }
      const body = (await res.json()) as ReportResponse;
      setReport(body.data);
      setStage('preview');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Report generation failed');
      setStage('config');
    }
  };

  const handleCopy = async () => {
    if (!report) return;
    await navigator.clipboard.writeText(report.markdown);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    if (!report) return;
    const blob = new Blob([report.markdown], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const dateStr = report.generated_at.slice(0, 10);
    a.href = url;
    a.download = `${slugify(brandName)}-report-${dateStr}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    setStage('idle');
    setReport(null);
    setError(null);
    setCopied(false);
    setRenderMode('rendered');
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setStage('config')}
        className="hub-btn hub-btn--ghost"
        data-testid="generate-report-btn"
      >
        <Icon name="bx-file-blank" />
        Generate report
      </button>

      {/* Config modal */}
      {stage === 'config' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="report-config-modal">
          <div className="w-full max-w-md rounded-xl border border-[var(--hub-color-line)] bg-white p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">Generate report</h2>
              <button onClick={handleClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600" aria-label="Close">
                <Icon name="bx-x" />
              </button>
            </div>
            <p className="mt-2 text-sm text-slate-500">
              Produces a markdown report for <span className="font-medium">{brandName}</span> covering quick wins, big wins, and current visibility. Hand the file to a Claude.ai session to render slides.
            </p>

            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700">Quick wins effort cap</label>
                <p className="mt-0.5 text-xs text-slate-500">Maximum effort estimate that still counts as a quick win.</p>
                <div className="mt-1">
                  <Select
                    options={EFFORT_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                    value={quickMaxEffort}
                    onChange={(v) => setQuickMaxEffort(v as typeof quickMaxEffort)}
                    className="w-full"
                    data-testid="report-quick-effort-select"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700">Big wins priority floor</label>
                <p className="mt-0.5 text-xs text-slate-500">Minimum playbook priority to qualify as a big win.</p>
                <div className="mt-1">
                  <Select
                    options={PRIORITY_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                    value={bigMinPriority}
                    onChange={(v) => setBigMinPriority(v as typeof bigMinPriority)}
                    className="w-full"
                    data-testid="report-big-priority-select"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700">Top N per section</label>
                <input
                  type="number"
                  min={1}
                  max={10}
                  value={topN}
                  onChange={(e) => setTopN(Math.min(Math.max(Number(e.target.value) || 5, 1), 10))}
                  className="mt-1 block w-24 rounded-lg border border-[var(--hub-color-line)] px-3 py-1.5 text-sm focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
                  data-testid="report-top-n-input"
                />
              </div>

              {error && (
                <p className="text-sm text-red-600" data-testid="report-config-error">{error}</p>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button onClick={handleClose} className="hub-btn hub-btn--ghost">
                Cancel
              </button>
              <button
                onClick={handleGenerate}
                className="hub-btn hub-btn--primary"
                data-testid="report-generate-submit"
              >
                <Icon name="bx-file-blank" />
                Generate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading overlay */}
      {stage === 'loading' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="rounded-xl bg-white px-8 py-6">
            <div className="flex items-center gap-3">
              <Spinner className="text-violet-600" />
              <span className="text-sm font-medium text-slate-700">Generating report…</span>
            </div>
            <p className="mt-2 text-xs text-slate-500">This calls OpenAI to write the executive summary — usually 5-15 seconds.</p>
          </div>
        </div>
      )}

      {/* Preview modal */}
      {stage === 'preview' && report && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="report-preview-modal">
          <div className="flex max-h-[90vh] w-full max-w-4xl flex-col rounded-xl border border-[var(--hub-color-line)] bg-white">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">{brandName} report</h2>
                <p className="mt-0.5 text-xs text-slate-500">
                  {report.counts.quick_wins} quick wins · {report.counts.big_wins} big wins · {report.counts.gaps} gaps · {report.counts.engines} engines · {report.counts.samples} samples
                </p>
              </div>
              <div className="flex items-center gap-2">
                {/* Spec 249: how the same report is shown is a parameter —
                    Kridt's Segmented control (report-mode-rendered/-raw). */}
                <Segmented
                  options={REPORT_MODES}
                  value={renderMode}
                  onChange={setRenderMode}
                  label="Show report as"
                  testId="report-mode"
                />
                <button
                  onClick={handleCopy}
                  className="hub-btn hub-btn--ghost"
                  data-testid="report-copy-btn"
                >
                  {copied ? <Icon name="bx-check" className="text-emerald-600" /> : <Icon name="bx-copy" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
                <button
                  onClick={handleDownload}
                  className="hub-btn hub-btn--primary"
                  data-testid="report-download-btn"
                >
                  <Icon name="bx-download" />
                  Download .md
                </button>
                <button onClick={handleClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600" aria-label="Close">
                  <Icon name="bx-x" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {renderMode === 'rendered' ? (
                <div className="prose prose-slate max-w-none prose-headings:scroll-mt-4 prose-table:text-sm prose-pre:bg-slate-100 prose-pre:text-slate-800" data-testid="report-rendered">
                  <ReactMarkdown>{report.markdown}</ReactMarkdown>
                </div>
              ) : (
                <pre className="whitespace-pre-wrap font-mono text-xs text-slate-800" data-testid="report-raw">
                  {report.markdown}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
